import { useState, useRef, useEffect } from "react";
import { PiCalendarBlankLight } from "react-icons/pi";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import DateRangePicker from "components/ui/date-range-picker";

type IndicatorsData = {
    totalOrders?: number;
    ordersDelivered?: number;
    ordersCancelled?: number;
    totalRevenue?: number;
};

type IndicatorsProps = {
    indicators?: IndicatorsData;
    onDateRangeChange?: (range: DateRange | undefined) => void;
    dateRange?: DateRange | undefined;
};

const INDICATOR_ITEMS: Array<{
    key: keyof IndicatorsData;
    label: string;
    isCurrency?: boolean;
}> = [
    { key: "totalOrders", label: "Orders" },
    { key: "ordersDelivered", label: "Orders Delivered" },
    { key: "ordersCancelled", label: "Orders Cancelled" },
    { key: "totalRevenue", label: "Total Revenue", isCurrency: true }
];

const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2
});

const formatValue = (value: number | undefined, isCurrency?: boolean) => {
    const normalizedValue = typeof value === "number" && Number.isFinite(value)
        ? value
        : 0;
    if (isCurrency) {
        return currencyFormatter.format(normalizedValue);
    }

    return normalizedValue.toLocaleString("en-US");
};

export default function Indicators({ indicators, onDateRangeChange, dateRange }: IndicatorsProps) {
    const data = indicators ?? {};
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(undefined);
    const [rangeLabel, setRangeLabel] = useState<string>("All Dates");
    const pickerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    // Sync with parent dateRange prop
    useEffect(() => {
        if (dateRange === undefined) {
            setSelectedRange(undefined);
            setRangeLabel("All Dates");
        }
    }, [dateRange]);

    // Close picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const isOutsidePicker = pickerRef.current && !pickerRef.current.contains(target);
            const isOutsideButton = buttonRef.current && !buttonRef.current.contains(target);
            
            if (isOutsidePicker && isOutsideButton) {
                setIsPickerOpen(false);
            }
        };

        if (isPickerOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isPickerOpen]);

    const handleDateChange = (range: DateRange | undefined, label?: string) => {
        setSelectedRange(range);
        
        if (label) {
            // Si viene un label desde el preset, usarlo
            setRangeLabel(label);
        } else if (range?.from && range?.to) {
            // Si es una selección manual del calendario, formatear las fechas
            const isSameDay = range.from.toDateString() === range.to.toDateString();
            if (isSameDay) {
                setRangeLabel(format(range.from, "MMM d, yyyy"));
            } else {
                setRangeLabel(`${format(range.from, "MMM d")} - ${format(range.to, "MMM d, yyyy")}`);
            }
        } else if (range?.from) {
            setRangeLabel(format(range.from, "MMM d, yyyy"));
        }
        
        onDateRangeChange?.(range);
    };

    return (
        <div className="orders_indicators" role="list" aria-label="Orders summary indicators">
            <div className="orders_indicator_item" role="listitem" style={{ position: "relative" }}>
                <div 
                    ref={buttonRef}
                    className="orders_filter_date" 
                    onClick={() => setIsPickerOpen(!isPickerOpen)}
                    style={{ cursor: "pointer" }}
                >
                    <div className="orders_filter_date_header">
                        <PiCalendarBlankLight size={20}/>
                        {rangeLabel !== "All Dates" && (
                            <a className="orders_filter_date_reset" onClick={(e) => {
                                e.stopPropagation();
                                handleDateChange(undefined, "All Dates");
                            }}>Reset</a>
                        )}
                    </div>
                    <span className="orders_indicator_label">{rangeLabel}</span>
                </div>
                
                {isPickerOpen && (
                    <div 
                        ref={pickerRef}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: "absolute",
                            top: "calc(100% + 8px)",
                            left: 0,
                            zIndex: 1000,
                        }}
                    >
                        <DateRangePicker
                            onDateChange={handleDateChange}
                            onClose={() => setIsPickerOpen(false)}
                        />
                    </div>
                )}
            </div>
            
            {INDICATOR_ITEMS.map(({ key, label, isCurrency }) => (
                <div key={key} className="orders_indicator_item" role="listitem">
                    <span className="orders_indicator_label">{label}</span>
                    <span className="orders_indicator_value">
                        {formatValue(data[key], isCurrency)}
                    </span>
                </div>
            ))}
        </div>
    );
}